import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Typography, Box, useTheme, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone, FileRejection } from 'react-dropzone';
import upload from 'packages/novous_web/src/assets/icons/upload-04.svg';
import file from 'packages/novous_web/src/assets/icons/Image File.svg';
import download from 'packages/novous_web/src/assets/icons/download.svg';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import { useToast } from 'packages/novous_web/src/context/ToastContext';
import { useExcelDownload } from 'packages/novous_web/src/utils/downloadUtils';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';

interface SubscriptionBulUploadProps {
  handleDriveDoc?: (file: string[]) => void;
  open: boolean;
  handleClose: () => void;
  uploadEndpoint?: string;
  maxFiles?: number;
  title?: string;
  fleetAdminId?: any;
  description?: string;
  entityType?: string;
  sampleFileUrl?: string;
  extraKeys?: Record<string, any>;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50%',
  height: '60%',
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: 24,
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  overflowY: 'auto',
};

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const DEFAULT_MAX_FILES = 2;

const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
  return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const SubscriptionBulUpload: React.FC<SubscriptionBulUploadProps> = ({
  open,
  handleClose,
  handleDriveDoc,
  uploadEndpoint = API_ENDPOINTS.directories.VEHICLE_BULK_UPLOAD,
  maxFiles = DEFAULT_MAX_FILES,
  title = 'Bulk Upload',
  fleetAdminId,
  description,
  entityType = 'vehicles',
  sampleFileUrl = '',
  extraKeys = {},
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const theme = useTheme();
  const [error, setError] = useState<string>('');
  const { user } = useAuth();
  const { showToast } = useToast();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [isUserSelected, setIsUserSelected] = useState<boolean>(false);
  const [selectedCompanyData, setSelectedCompanyData] = useState<any>(null);

  const defaultDescription = `Upload an Excel/CSV files to add multiple ${entityType} at once!`;
  const finalDescription = description || defaultDescription;
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const {
    data: uploadData,
    error: uploadError,
    loading: uploadLoading,
    fetchData: fetchUploadData,
  } = useAxios<any>(uploadEndpoint, 'POST');

  // For company list (ROLE_SUPER_ADMIN)
  const {
    data: companyList,
    error: companyListError,
    loading: companyListLoading,
    fetchData: fetchCompanyListData,
  } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_LIST}`, 'GET');

  // For user list by company (ROLE_SUPER_ADMIN -> company -> users)
  const {
    data: companyUsersList,
    error: companyUsersListError,
    loading: companyUsersListLoading,
    fetchData: fetchCompanyUsersListData,
  } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, 'POST');

  // For direct user list (ROLE_ADMIN)
  const {
    data: directUserList,
    error: directUserListError,
    loading: directUserListLoading,
    fetchData: fetchDirectUserListData,
  } = useAxios<any>(`${API_ENDPOINTS.users.CUSTOMER_LIST_CRN}`, 'POST');

  const { downloadFile: downloadSampleFile, loading: sampleFileLoading } = useExcelDownload({
    url: sampleFileUrl,
    filename: `sample_${entityType}_template.xlsx`,
    successMessage: 'Sample file downloaded successfully!',
    errorMessage: 'Failed to download sample file',
  });

  // ROLE_SUPER_ADMIN के लिए company list fetch करें
  useEffect(() => {
    if (fleetAdminId?.authorities === 'ROLE_SUPER_ADMIN') {
      fetchCompanyListData();
    }
  }, [fleetAdminId]);

  // ROLE_ADMIN के लिए direct user list fetch करें
  useEffect(() => {
    if (fleetAdminId?.authorities === 'ROLE_ADMIN') {
      fetchDirectUserListData({ userCrn: fleetAdminId?.crn, userId: fleetAdminId?.id });
    }
  }, [fleetAdminId]);

  // ROLE_SUPER_ADMIN के लिए selected company के users fetch करें
  useEffect(() => {
    if (fleetAdminId?.authorities === 'ROLE_SUPER_ADMIN' && selectedCompanyId) {
      const companySelected = companyList?.data?.find((company: any) => company.id === selectedCompanyId);
      setSelectedCompanyData(companySelected || null);
      if (companySelected) {
        fetchCompanyUsersListData({
          userCrn: companySelected.crn,
          userId: companySelected.id,
        });
      }
    }
  }, [selectedCompanyId, companyList?.data, fleetAdminId]);

  useEffect(() => {
    if (uploadData) {
      const message = uploadData?.message || uploadData?.data?.message || 'Upload completed successfully!';
      const type = 'success';
      showToast(message, type);
      setIsSuccessModalOpen(true);
      handleClose();
      setFiles([]);
      setSelectedCustomerId('');
      setSelectedCompanyId('');
      setSelectedCompanyData(null);
      setIsUserSelected(false);
    }
  }, [uploadData, handleClose, showToast]);

  useEffect(() => {
    if (uploadError) {
      const errorMessage = uploadError || 'Something went wrong!';
      showToast(errorMessage, 'error');
      handleClose();
    }
  }, [uploadError, handleClose, showToast]);

  const handleSampleDownload = async () => {
    if (!sampleFileUrl) return;
    await downloadSampleFile();
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setError('');

      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const reason = rejection.errors[0].code;

        if (reason === 'file-invalid-type') {
          setError('Only CSV and Excel files are accepted.');
        } else if (reason === 'file-too-large') {
          setError(`Each file must be less than ${MAX_FILE_SIZE_MB}MB.`);
        } else if (reason === 'too-many-files') {
          setError(`You can upload a maximum of ${maxFiles} files.`);
        } else {
          setError('An unknown error occurred. Please check your files.');
        }
        return;
      }

      if (files.length + acceptedFiles.length > maxFiles) {
        setError(`You can upload a maximum of ${maxFiles} files.`);
        return;
      }

      const oversizedFiles = acceptedFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES);

      if (oversizedFiles.length > 0) {
        setError(`Each file must be less than ${MAX_FILE_SIZE_MB}MB.`);
        return;
      }

      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
    },
    [files, maxFiles],
  );

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: maxFiles - files.length,
  });

  const handleCompanyChange = (value: string) => {
    setSelectedCompanyId(value);
    setSelectedCustomerId('');
    setIsUserSelected(false);
    setSelectedCompanyData(null);
  };

  const handleCustomerChange = (value: string) => {
    setSelectedCustomerId(value);
    setIsUserSelected(!!value);
  };

  console.log(selectedCompanyId, fleetAdminId?.id, 'scnksncksncksnckscnkscnkscnk');

  const handleUpload = () => {
    // ROLE_SUPER_ADMIN के लिए validation
    if (fleetAdminId?.authorities === 'ROLE_SUPER_ADMIN') {
      if (!selectedCompanyId) {
        showToast('Please select a company', 'error');
        return;
      }
      if (!selectedCustomerId) {
        showToast('Please select a customer', 'error');
        return;
      }
    }

    // ROLE_ADMIN के लिए validation
    if (fleetAdminId?.authorities === 'ROLE_ADMIN' && !selectedCustomerId) {
      showToast('Please select a customer', 'error');
      return;
    }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append('file', file);
    });

    // Determine fleetId based on role
    let fleetId = '';
    if (fleetAdminId?.authorities === 'ROLE_SUPER_ADMIN') {
      fleetId = selectedCustomerId;
    } else if (fleetAdminId?.authorities === 'ROLE_ADMIN') {
      fleetId = selectedCustomerId;
    } else {
      fleetId = user?.customer_id || '';
    }

    // Determine fleetAdminId based on role
    let adminId = '';
    if (fleetAdminId?.authorities === 'ROLE_SUPER_ADMIN') {
      // ROLE_SUPER_ADMIN के लिए selected company का id (NOT the user's parentId)
      adminId = selectedCompanyId;
    } else if (fleetAdminId?.authorities === 'ROLE_ADMIN') {
      // ROLE_ADMIN के लिए fleetAdminId का id
      adminId = fleetAdminId?.id || '';
    } else {
      // अन्य roles के लिए user का parentId
      adminId = user?.parentId || '';
    }

    const requestData = {
      fleetId: fleetId,
      fleetAdminId: adminId,
      ...extraKeys,
    };

    console.log('Request Data:', requestData);

    formData.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));

    fetchUploadData(formData);
  };

  // Check which dropdowns to show based on role
  const isSuperAdmin = fleetAdminId?.authorities === 'ROLE_SUPER_ADMIN';
  const isAdmin = fleetAdminId?.authorities === 'ROLE_ADMIN';
  const showCustomerDropdown = isSuperAdmin || isAdmin;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="upload-bulk-title"
      aria-describedby="upload-bulk-description"
    >
      <Box sx={modalStyle}>
        <div className="flex gap-4 flex-wrap justify-between">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography id="upload-bulk-title" variant="h6">
              {title}
            </Typography>
            <Typography id="upload-bulk-description" variant="button" fontWeight={400} color="secondary">
              {finalDescription}
            </Typography>
          </div>
          <div className="flex gap-4 items-center flex-1 justify-between">
            <Box className="flex-1 flex gap-4">
              {/* ROLE_SUPER_ADMIN के लिए Company Dropdown */}
              {isSuperAdmin && (
                <CustomDropDownInput
                  options={
                    companyList?.data?.map((company: any) => ({
                      value: company.id,
                      label: company.name,
                    })) || []
                  }
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  placeholder="Select Company"
                  showDropdownArrow={true}
                  isMandatory
                />
              )}

              {/* Customer Dropdown (ROLE_SUPER_ADMIN या ROLE_ADMIN के लिए) */}
              {showCustomerDropdown && (
                <CustomDropDownInput
                  options={
                    isSuperAdmin
                      ? companyUsersList?.data?.map((user: any) => ({
                          value: user.customer_id, // customer_id as value
                          label: user.fullName, // fullName as label
                        })) || []
                      : directUserList?.data?.map((user: any) => ({
                          value: user.customer_id, // customer_id as value
                          label: user.fullName, // fullName as label
                        })) || []
                  }
                  value={selectedCustomerId}
                  onChange={handleCustomerChange}
                  placeholder="Select Customer"
                  showDropdownArrow={true}
                  isDisabled={isSuperAdmin && !selectedCompanyId}
                  isMandatory
                />
              )}
            </Box>
            <Box>
              {sampleFileUrl && (
                <CustomIconButton
                  icon={<img src={download} alt="sample-file" width={16} height={16} />}
                  text="Sample File"
                  background="transparent"
                  variant="outlined"
                  onClick={handleSampleDownload}
                />
              )}
            </Box>
          </div>
        </div>

        {files.length === 0 && (
          <div
            {...getRootProps()}
            style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '32px',
              height: '100%',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#f5f5f5' : '#fff',
              transition: 'background-color 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <div>
              <div className="flex gap-1 items-center justify-center">
                <input {...getInputProps()} />
                <img src={upload} alt="upload" width={24} height={24} />
                <Typography variant="overline" color={theme.palette.primary.main} fontWeight={700}>
                  Drop or select files to upload
                </Typography>
              </div>
              <Typography variant="caption" color="secondary">
                (CSV, Excel only) — Max {maxFiles} files, {MAX_FILE_SIZE_MB}MB each
              </Typography>
            </div>
          </div>
        )}

        {files.length > 0 && (
          <div className="h-full">
            <div className="flex flex-wrap gap-4 ">
              {files.map((file, index) => (
                <UploadedFile
                  key={index}
                  name={file.name}
                  size={file.size}
                  onRemove={() => removeFile(index)}
                />
              ))}
            </div>
          </div>
        )}

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <div className="flex justify-between gap-1">
          <CustomIconButton
            text="Cancel"
            background="transparent"
            outline={true}
            padding="0px"
            variant="outlined"
            onClick={handleClose}
          />
          <CustomIconButton
            text="Upload"
            outline={false}
            padding="0px"
            isDisabled={
              files.length === 0 ||
              uploadLoading ||
              (showCustomerDropdown && !selectedCustomerId) ||
              (isSuperAdmin && !selectedCompanyId)
            }
            variant="contained"
            onClick={handleUpload}
          />
        </div>

        {/* Success Modal */}
        <Modal
          open={isSuccessModalOpen}
          onClose={() => setIsSuccessModalOpen(false)}
          aria-labelledby="bulk-upload-success-title"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: 24,
              padding: '32px',
              minWidth: 320,
              textAlign: 'center',
            }}
          >
            <Typography id="bulk-upload-success-title" variant="h6" fontWeight={700} mb={2}>
              Bulk Upload Successful
            </Typography>
            <Typography variant="body1" mb={2}>
              Your file(s) have been uploaded successfully!
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setIsSuccessModalOpen(false)}>
              OK
            </Button>
          </Box>
        </Modal>
      </Box>
    </Modal>
  );
};

export default SubscriptionBulUpload;

interface UploadedFileProps {
  name: string;
  size: number;
  onRemove: () => void;
}

const UploadedFile: React.FC<UploadedFileProps> = ({ name, size, onRemove }) => {
  return (
    <div className="flex p-4 gap-4 w-[220px] items-center border rounded-md relative">
      <img src={file} alt="file" width={32} height={32} />
      <div className="flex flex-col gap-1 w-full overflow-x-hidden">
        <Typography className="truncate" variant="body2" fontWeight={400}>
          {name}
        </Typography>
        <Typography variant="body2" color="secondary" fontWeight={400}>
          {formatFileSize(size)}
        </Typography>
      </div>
      <IconButton
        size="small"
        onClick={onRemove}
        sx={(theme) => ({
          position: 'absolute',
          top: -12,
          right: -10,
          backgroundColor: theme.palette.error.main,
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
          },
          borderRadius: '20%',
          padding: '2px',
        })}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};
